import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { PDFFindController } from "./PDFFindController/PDFFindController";
const defaultSearchContextValue = {
    totalResults: 0,
    isSearching: false,
    pageMatches: [],
    pageMatchesLength: [],
    currentResultIndex: -1,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setCurrentResultIndex: ()=>{},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSearchPhraseChange: ()=>{},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    clearSearch: ()=>{},
    getMatchesMapArray: ()=>[]
};
const PDFViewerSearchContext = /*#__PURE__*/ createContext(defaultSearchContextValue);
export const PDFViewerSearchContextProvider = /*#__PURE__*/ memo((param)=>{
    let { children, pdfDocument, pdfPagesCache } = param;
    const findControllerRef = useRef();
    const [totalResults, setTotalResults] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const [pageMatches, setPageMatches] = useState([]);
    const [pageMatchesLength, setPageMatchesLength] = useState([]);
    const [currentResultIndex, setCurrentResultIndex] = useState(-1);
    const matchesMapArrayRef = useRef([]);
    useEffect(()=>{
        if (pdfDocument && pdfPagesCache) {
            findControllerRef.current = new PDFFindController(pdfDocument, pdfPagesCache);
            return ()=>{
                var _findControllerRef_current;
                (_findControllerRef_current = findControllerRef.current) === null || _findControllerRef_current === void 0 ? void 0 : _findControllerRef_current.cleanup();
            };
        }
    }, [
        pdfDocument,
        pdfPagesCache
    ]);
    const onSearchPhraseChange = useCallback(async (phrase)=>{
        // Return early if PDF is not loaded
        if (!pdfDocument || !pdfPagesCache) {
            return 0;
        }
        flushSync(()=>{
            setIsSearching(true);
            setTotalResults(0);
            setCurrentResultIndex(-1);
            setPageMatchesLength([]);
            setPageMatches([]);
        });
        const findController = findControllerRef.current;
        if (!findController) return;
        findController.resetSearch();
        try {
            const { totalResults, pageMatchesLength, pageMatches } = await findController.searchInPdf(phrase);
            matchesMapArrayRef.current.length = 0;
            for(let pageIndex = 0; pageIndex < pageMatches.length; pageIndex++){
                for(let divIndex = 0; divIndex < pageMatches[pageIndex].length; divIndex++){
                    matchesMapArrayRef.current.push({
                        pageIndex,
                        divIndex
                    });
                }
            }
            if (totalResults > 0) {
                setCurrentResultIndex(0);
            }
            setTotalResults(totalResults);
            setPageMatchesLength([
                ...pageMatchesLength
            ]);
            setPageMatches([
                ...pageMatches
            ]);
            return totalResults;
        } finally{
            setIsSearching(false);
        }
    }, [
        pdfDocument,
        pdfPagesCache
    ]);
    const clearSearch = useCallback(()=>{
        var _findControllerRef_current;
        setTotalResults(0);
        setCurrentResultIndex(-1);
        setPageMatches([]);
        setPageMatchesLength([]);
        matchesMapArrayRef.current.length = 0;
        (_findControllerRef_current = findControllerRef.current) === null || _findControllerRef_current === void 0 ? void 0 : _findControllerRef_current.resetSearch();
    }, []);
    const getMatchesMapArray = useCallback(()=>matchesMapArrayRef.current, []);
    const value = useMemo(()=>({
            onSearchPhraseChange,
            totalResults,
            isSearching,
            pageMatches,
            pageMatchesLength,
            currentResultIndex,
            setCurrentResultIndex,
            getMatchesMapArray,
            clearSearch
        }), [
        onSearchPhraseChange,
        totalResults,
        isSearching,
        pageMatches,
        pageMatchesLength,
        currentResultIndex,
        getMatchesMapArray,
        clearSearch
    ]);
    return /*#__PURE__*/ _jsx(PDFViewerSearchContext.Provider, {
        value: value,
        children: children
    });
});
PDFViewerSearchContextProvider.displayName = "PDFViewerSearchContextProvider";
export const usePDFViewerSearchContext = ()=>{
    const context = useContext(PDFViewerSearchContext);
    if (!context) {
        throw new Error("usePDFViewerSearchContext must be used within a PDFViewerSearchProvider");
    }
    return context;
};
